/**
 * Student Profile View
 * Ficha del alumno (diseño «Q» para el personal y «R» para el propio alumno, ver lienzo de diseño).
 * Una sola vista con dos caras: el personal revisa al alumno (editar, promocionar, acceso, documentos);
 * el alumno la abre como Inicio para ver qué tiene hoy y qué le falta por hacer. Pestañas como botones,
 * columna derecha fija y datos del servidor cargados una vez por ficha. Estilos .spf-* en
 * assets/css/views/student-profile.css.
 */
const StudentProfileView = {
    studentId: null,
    tab: 'resumen',
    attFilter: 'all',
    _data: null,

    isOwn() {
        return typeof AuthManager !== 'undefined' && AuthManager.isStudent();
    },

    student() {
        return StudentService.getStudents().find(s => s.id === this.studentId);
    },

    canManage() {
        return typeof StudentView !== 'undefined' && StudentView.canAddStudents();
    },

    fmtDate(value, opts = { day: 'numeric', month: 'short', year: 'numeric' }) {
        const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
        const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(value);
        return isNaN(d) ? '' : d.toLocaleDateString('es-ES', opts);
    },

    pill(text, tone) {
        return `<span class="spf-pill is-${tone}">${Utils.sanitizeHTML(text)}</span>`;
    },

    render(studentId) {
        if (studentId !== this.studentId) { this.tab = 'resumen'; this.attFilter = 'all'; this._data = null; }
        this.studentId = studentId;
        const student = this.student();
        if (!student) {
            this.fetchAndRender(studentId);
            return `<div class="view-container dnr-proposal fade-in" id="student-profile-container"><div class="dnr-empty" style="padding:4rem;"><i class="fas fa-spinner fa-spin" style="font-size:2rem; color:var(--dnr-teal-600);"></i><p style="margin-top:1rem;">Cargando ficha del alumno...</p></div></div>`;
        }
        const own = this.isOwn();
        const absent = this._getAttendanceRecords().filter(r => r.status === 'absent').length;
        const tabs = [['resumen', own ? 'Hoy' : 'Resumen', ''], ['asistencia', 'Asistencia', absent ? String(absent) : ''], ['notas', 'Notas', ''],
            ['trabajos', 'Trabajos', ''], ['tutorias', 'Tutorías', ''], ['datos', own ? 'Mis datos' : 'Datos personales', '']];
        return `
            <div class="view-container dnr-proposal fade-in spf-page" id="student-profile-container">
                ${this.renderHeader(student)}
                <nav class="spf-sections" aria-label="Apartados de la ficha">
                    ${tabs.map(([id, label, badge]) => `<button type="button" id="tab-btn-${id}" class="spf-sec" aria-pressed="false" ${UIEvents.attr("click", function(event,captured){StudentProfileView.switchTab(String(captured[0]));}, [id])}>${label}<span class="spf-sec-badge" id="spf-badge-${id}" ${badge ? '' : 'hidden'}>${badge}</span></button>`).join('')}
                </nav>
                <div class="spf-layout">
                    <div class="spf-main" id="student-tab-content"></div>
                    <aside class="spf-side" id="spf-side">${this.renderAside(student)}</aside>
                </div>
            </div>
        `;
    },

    renderHeader(student) {
        const own = this.isOwn();
        const formations = (student.formations || []).map(id => FormationService.getFormations().find(f => f.id === id)).filter(Boolean);
        const initials = ((student.firstName || 'A').charAt(0) + (student.lastName || 'L').charAt(0)).toUpperCase();
        const currentUser = AuthManager.getCurrentUser();
        // The self-service portrait lives on User, not on the student document record.
        // Never borrow the signed-in user's picture for somebody else's file.
        const photo = (currentUser?.studentId === student.id && currentUser.photo) || student.profilePhoto || student.photo;
        const photoAttributes = photo ? StudentPhotos.attributes(photo) : '';
        const fullName = `${student.firstName || ''} ${student.lastName || ''}`.trim();
        const yearStart = typeof StudentView !== 'undefined' ? StudentView.yearOf(student) : null;
        const course = yearStart ? `Curso ${yearStart} / ${yearStart + 1}` : '';
        const formationText = formations.map(f => Utils.sanitizeHTML(f.name)).join(' · ');
        const action = (icon, label, fn, args, primary) => `<button type="button" class="spf-btn${primary ? ' is-primary' : ''}" ${UIEvents.attr("click", fn, args)}><i class="fas fa-${icon}" aria-hidden="true"></i>${label}</button>`;
        const go = function(event,captured){App.navigate(String(captured[0]));};
        const actions = own ? [
            action('comments', 'Pedir tutoría', go, ['tutorias']),
            action('file-circle-check', 'Justificar una falta', go, ['justificantes']),
            action('camera', 'Pedir material', go, ['almacen']),
            action('upload', 'Entregar trabajo', go, ['tareas'], true)
        ] : [
            AuthManager.isAdmin() ? action('warehouse', 'Permiso de almacén', function(event,captured){StudentWarehousePermission.open(String(captured[0]));}, [student.id]) : '',
            this.canManage() ? action('arrow-right', 'Promocionar', function(event,captured){StudentView.openPromoteModal([String(captured[0])]);}, [student.id]) : '',
            this.canManage() ? action('pen', 'Editar ficha', function(event,captured){StudentView.openEditModal(String(captured[0]));}, [student.id], true) : ''
        ];
        const today = new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'short' });
        return `
            <header class="spf-top">
                <div class="spf-id">
                    ${own ? '' : `<button type="button" class="spf-back" aria-label="Volver a Alumnado" title="Volver a Alumnado" ${UIEvents.attr("click", function(event,captured){App.navigate('alumnado');}, [])}><i class="fas fa-arrow-left" aria-hidden="true"></i></button>`}
                    <div class="spf-avatar">
                        ${photoAttributes ? `<img ${photoAttributes} alt="Foto de ${Utils.sanitizeHTML(fullName)}">` : Utils.sanitizeHTML(initials)}
                        ${own ? '' : `<span class="spf-avatar-dot ${student.active !== false ? 'is-active' : 'is-inactive'}"></span>`}
                    </div>
                    <div class="spf-id-text">
                        <span class="spf-eyebrow">${own ? `Mi curso${course ? ' · ' + course.replace('Curso ', '') : ''} · ${Utils.sanitizeHTML(today)}` : `Alumnado · Ficha del alumno${course ? ' · ' + course : ''}`}</span>
                        <h1 class="spf-name">${Utils.sanitizeHTML(own ? `Hola, ${student.firstName || ''}` : fullName)}</h1>
                        <div class="spf-meta">
                            ${own ? '' : this.pill(student.active !== false ? 'Activo' : 'Inactivo', student.active !== false ? 'ok' : 'soft')}
                            ${formationText ? `<span><strong>${formationText}</strong>${!own && student.enrollmentDate ? ` · desde ${Utils.sanitizeHTML(this.fmtDate(student.enrollmentDate))}` : ''}</span>` : '<span>Sin formación asignada</span>'}
                            ${own ? '' : [student.email, student.phone].filter(Boolean).map(v => `<span>${Utils.sanitizeHTML(v)}</span>`).join('')}
                            ${!own && student.dni ? `<span class="spf-mono">${Utils.sanitizeHTML(student.dni)}</span>` : ''}
                        </div>
                    </div>
                </div>
                <div class="spf-acts">${actions.join('')}</div>
            </header>`;
    },

    init() {
        this.switchTab(this.tab || 'resumen');
        this.loadData();
    },

    // Repaint after a change made from this page (edit, promote, access): keeps the open tab.
    refresh() {
        const area = document.getElementById('content-area');
        if (!area || !this.student()) return;
        SafeHTML.set(area, this.render(this.studentId));
        this.switchTab(this.tab);
        this.paintData();
    },

    switchTab(tabId) {
        this.tab = tabId;
        ['resumen','asistencia','notas','trabajos','datos','tutorias'].forEach(id => {
            const btn = document.getElementById('tab-btn-' + id);
            if (!btn) return;
            btn.classList.toggle('is-on', id === tabId);
            btn.setAttribute('aria-pressed', String(id === tabId));
        });
        const container = document.getElementById('student-tab-content');
        if (!container) return;
        switch(tabId) {
            case 'resumen': SafeHTML.set(container, this.renderResumen()); this.paintData(); break;
            case 'asistencia': SafeHTML.set(container, this.renderAsistencia()); break;
            case 'notas': SafeHTML.set(container, this.renderNotas()); this.loadNotas(); break;
            case 'trabajos': SafeHTML.set(container, this.renderTrabajos()); break;
            case 'datos': SafeHTML.set(container, this.renderDatos()); break;
            case 'tutorias': SafeHTML.set(container, this.renderTutorias()); break;
        }
    },

    // Tareas, entregas, tutorías y notas del alumno: una carga por ficha, que reparten el
    // resumen, las pestañas y la columna derecha.
    async loadData() {
        const id = this.studentId;
        const headers = { 'X-SICAF-CSRF': AuthManager.getCsrfToken() };
        const json = (url, extra = {}) => fetch(url, { headers, ...extra }).then(r => r.ok ? r.json() : null).catch(() => null);
        const [tasks, works, tutorias, grades] = await Promise.all([
            json('/api/tasks'),
            json('/api/student-works/' + encodeURIComponent(id)),
            json('/api/tutorias?studentId=' + encodeURIComponent(id)),
            json('/api/calificaciones?studentId=' + encodeURIComponent(id), { cache: 'no-store' })
        ]);
        if (id !== this.studentId) return;
        this._data = {
            tasks: tasks?.success ? (tasks.tasks || []) : null,
            works: works?.success ? (works.works || []) : null,
            tutorias: tutorias?.success ? (tutorias.tutorias || []).filter(t => t.studentId === id) : null,
            grades: grades?.success && Array.isArray(grades.calificaciones) ? grades.calificaciones.filter(c => c?.nota?.studentId === id) : null
        };
        this.paintData();
        if (this.tab === 'trabajos' || this.tab === 'tutorias') this.switchTab(this.tab);
    },

    // Tareas del alumno (de su grupo o solo para él) con su entrega, si la hay.
    taskSummary() {
        const d = this._data;
        if (!d || !d.tasks || !d.works) return null;
        const student = this.student();
        const mine = (student?.formations || []).map(String);
        const now = new Date();
        const tasks = d.tasks.filter(t => (t.formationIds || []).some(f => mine.includes(String(f))) || (t.studentIds || []).map(String).includes(String(this.studentId)));
        const rows = tasks.map(task => {
            const work = d.works.find(w => w.taskId === task.id);
            const deadline = task.deadline ? new Date(task.deadline) : null;
            return { task, work, deadline, late: !work && deadline && deadline < now, lateDelivery: work && deadline && new Date(work.uploadedAt) > deadline };
        });
        const pending = rows.filter(r => !r.work).sort((a, b) => (a.deadline || Infinity) - (b.deadline || Infinity));
        return { pending, delivered: rows.filter(r => r.work), late: pending.filter(r => r.late) };
    },

    latestGrade() {
        const grades = this._data?.grades;
        if (!grades || !grades.length) return null;
        const order = { '1Trimestre': 1, '2Trimestre': 2, '3Trimestre': 3, 'Final': 4 };
        const periods = { '1Trimestre': '1.er trimestre', '2Trimestre': '2.º trimestre', '3Trimestre': '3.er trimestre', 'Final': 'Evaluación final' };
        const acta = [...grades].sort((a, b) => String(b.year || '').localeCompare(String(a.year || '')) || (order[b.period] || 0) - (order[a.period] || 0))[0];
        const criteria = ['tecnica', 'lenguaje', 'compromiso', 'colectivo', 'adaptabilidad'];
        const flagged = criteria.filter(k => ['AVISO', 'NO APTO'].includes(acta.nota[k])).length;
        return { value: acta.nota.calificacion, period: periods[acta.period] || acta.period || '', flagged };
    },

    paintData() {
        const set = (id, html) => { const el = document.getElementById(id); if (el) SafeHTML.set(el, html); };
        const tasks = this.taskSummary();
        if (tasks) {
            set('spf-kpi-tasks', `<span class="spf-kpi-value">${tasks.delivered.length} <small>${tasks.delivered.length === 1 ? 'entregada' : 'entregadas'}</small></span>
                <span class="spf-kpi-sub">${tasks.pending.length} ${tasks.pending.length === 1 ? 'pendiente' : 'pendientes'}${tasks.late.length ? ` · ${tasks.late.length} fuera de plazo` : ''}</span>`);
            set('spf-pending-tasks', this.renderPendingTasks(tasks));
            const badge = document.getElementById('spf-badge-trabajos');
            if (badge) { badge.textContent = tasks.pending.length ? String(tasks.pending.length) : ''; badge.hidden = !tasks.pending.length; }
        } else if (this._data) {
            set('spf-kpi-tasks', '<span class="spf-kpi-value">—</span><span class="spf-kpi-sub">No se han podido cargar</span>');
            set('spf-pending-tasks', '<p class="spf-note">No se han podido cargar las tareas.</p>');
        }
        if (this._data) {
            const g = this.latestGrade();
            const label = { 'APTO': 'Apto', 'AVISO': 'Aviso', 'NO APTO': 'No apto' };
            const tone = { 'APTO': 'ok', 'AVISO': 'warn', 'NO APTO': 'bad' };
            set('spf-kpi-grade', g ? `<span class="spf-kpi-value is-${tone[g.value] || 'soft'}">${Utils.sanitizeHTML(label[g.value] || 'Sin evaluar')}</span>
                <span class="spf-kpi-sub">${Utils.sanitizeHTML(g.period)}${g.flagged ? ` · ${g.flagged} ${g.flagged === 1 ? 'aviso' : 'avisos'} en criterios` : ''}</span>`
                : '<span class="spf-kpi-value">—</span><span class="spf-kpi-sub">Todavía sin actas publicadas</span>');
            set('spf-todo', this.renderTodo(tasks));
        }
    },

    async fetchAndRender(studentId) {
        try {
            const token = AuthManager.getCsrfToken() || (typeof AuthManager !== 'undefined' ? AuthManager.getCsrfToken() : '');
            const res = await fetch(`/api/students/${studentId}`, { headers: { 'X-SICAF-CSRF': token } });
            if (res.ok) {
                const data = await res.json();
                if (data.success && data.student) {
                    if (typeof DataManager !== 'undefined') {
                        const current = DataManager.getAll(DataManager.KEYS.STUDENTS) || [];
                        if (!current.some(s => s.id === data.student.id)) {
                            DataManager.saveAll(DataManager.KEYS.STUDENTS, [...current, data.student]);
                        }
                    }
                    if (typeof FileStorage !== 'undefined' && FileStorage.dataStore) {
                        if (!FileStorage.dataStore.students) FileStorage.dataStore.students = [];
                        if (!FileStorage.dataStore.students.some(s => s.id === data.student.id)) {
                            FileStorage.dataStore.students.push(data.student);
                        }
                    }
                    const container = document.getElementById('content-area');
                    if (container) {
                        SafeHTML.set(container, this.render(studentId));
                        this.init();
                        if (typeof App !== 'undefined') {
                            if (App.updateBreadcrumbs) {
                                App.updateBreadcrumbs(`alumno/${studentId}`);
                            }
                            if (typeof Utils !== 'undefined' && Utils.studentProfileUrl) {
                                const friendlyRoute = Utils.studentProfileUrl(data.student);
                                if (friendlyRoute && App.setRoute) {
                                    App.setRoute(friendlyRoute, { replace: true });
                                }
                            }
                        }
                    }
                    return;
                }
            }
            const container = document.getElementById('student-profile-container');
            if (container) {
                SafeHTML.set(container, `<div class="alert alert-danger" style="margin:2rem;">Alumno no encontrado.</div>`);
            }
        } catch (e) {
            console.error('Error fetching student profile:', e);
            const container = document.getElementById('student-profile-container');
            if (container) {
                SafeHTML.set(container, `<div class="alert alert-danger" style="margin:2rem;">Error al cargar la ficha del alumno.</div>`);
            }
        }
    },

    renderResumen() {
        const own = this.isOwn();
        const records = this._getAttendanceRecords();
        const present = records.filter(r => r.status === 'present').length;
        const absent = records.filter(r => r.status === 'absent').length;
        const justified = records.filter(r => r.status === 'justified').length;
        const rate = records.length ? Math.round(present / records.length * 100) : null;
        const kpis = `
            <div class="spf-kpis">
                <div class="spf-kpi">
                    <span class="spf-kpi-label">Asistencia</span>
                    <span class="spf-kpi-value">${rate !== null ? rate + ' %' : '—'}</span>
                    ${rate !== null ? `<span class="spf-bar" role="img" aria-label="${present} de ${records.length} sesiones"><span style="width:${rate}%"></span></span>` : ''}
                    <span class="spf-kpi-sub">${records.length ? `${present} de ${records.length} sesiones` : 'Sin sesiones registradas'}</span>
                </div>
                <div class="spf-kpi${absent ? ' is-alert' : ''}">
                    <span class="spf-kpi-label">Faltas</span>
                    <span class="spf-kpi-value">${absent + justified}</span>
                    <span class="spf-kpi-sub">${absent ? `<strong>${absent} sin justificar</strong>` : 'Ninguna sin justificar'} · ${justified} ${justified === 1 ? 'justificada' : 'justificadas'}${own && absent ? ` · <button type="button" class="spf-link" ${UIEvents.attr("click", function(event,captured){App.navigate('justificantes');}, [])}>justificar</button>` : ''}</span>
                </div>
                <div class="spf-kpi">
                    <span class="spf-kpi-label">Tareas</span>
                    <span id="spf-kpi-tasks" class="spf-kpi-body"><span class="spf-kpi-value">…</span><span class="spf-kpi-sub">Cargando…</span></span>
                </div>
                <div class="spf-kpi">
                    <span class="spf-kpi-label">Última evaluación</span>
                    <span id="spf-kpi-grade" class="spf-kpi-body"><span class="spf-kpi-value">…</span><span class="spf-kpi-sub">Cargando…</span></span>
                </div>
            </div>`;
        const strip = this.renderStrip(records);
        const pair = `
            <div class="spf-two">
                <section class="spf-card" aria-labelledby="spf-today-title">
                    <h2 class="spf-h2" id="spf-today-title">${own ? 'Mis clases de hoy' : 'Hoy · ' + Utils.sanitizeHTML(new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'short' }))}</h2>
                    ${this.renderTodaysClasses()}
                </section>
                <section class="spf-card" aria-labelledby="spf-tasks-title">
                    <div class="spf-card-head">
                        <h2 class="spf-h2" id="spf-tasks-title">${own ? 'Por entregar' : 'Tareas pendientes'}</h2>
                        <button type="button" class="spf-link" ${UIEvents.attr("click", function(event,captured){StudentProfileView.switchTab('trabajos');}, [])}>${own ? 'Mis trabajos' : 'Trabajos'} ›</button>
                    </div>
                    <div id="spf-pending-tasks"><p class="spf-note">Cargando tareas…</p></div>
                </section>
            </div>`;
        // El alumno ve primero lo que tiene que hacer hoy; el personal, primero las cifras.
        return own ? pair + kpis + strip : kpis + strip + pair;
    },

    renderStrip(records) {
        const last = [...records].filter(r => r.date).sort((a, b) => String(a.date).localeCompare(String(b.date))).slice(-20);
        const word = { present: 'Asistió', absent: 'Faltó', justified: 'Justificada' };
        return `
            <section class="spf-card" aria-labelledby="spf-strip-title">
                <div class="spf-card-head">
                    <h2 class="spf-h2" id="spf-strip-title">Últimas sesiones</h2>
                    <button type="button" class="spf-link" ${UIEvents.attr("click", function(event,captured){StudentProfileView.switchTab('asistencia');}, [])}>${this.isOwn() ? 'Mi asistencia' : 'Ver toda la asistencia'} ›</button>
                </div>
                ${last.length ? `
                <ol class="spf-strip" style="--spf-cells:${last.length}">
                    ${last.map(r => `<li class="spf-strip-cell is-${r.status}" title="${Utils.sanitizeHTML(this.fmtDate(r.date))}: ${word[r.status]}"><span class="spf-visually-hidden">${Utils.sanitizeHTML(this.fmtDate(r.date))}: ${word[r.status]}</span></li>`).join('')}
                </ol>
                <div class="spf-legend">
                    <span><i class="is-present"></i>Asistió</span><span><i class="is-justified"></i>Justificada</span><span><i class="is-absent"></i>Faltó</span>
                    <span class="spf-legend-range">Del ${Utils.sanitizeHTML(this.fmtDate(last[0].date, { day: 'numeric', month: 'short' }))} al ${Utils.sanitizeHTML(this.fmtDate(last[last.length - 1].date, { day: 'numeric', month: 'short' }))} · la más reciente a la derecha</span>
                </div>` : '<p class="spf-note">Todavía no hay sesiones con asistencia registrada.</p>'}
            </section>`;
    },

    renderTodaysClasses() {
        const student = this.student();
        const mine = (student?.formations || []).map(String);
        const classes = (typeof ClassService !== 'undefined' ? ClassService.getClasses() : []).filter(c => mine.includes(String(c.formationId)));
        const now = new Date();
        const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        const describe = c => {
            const subject = typeof SubjectService !== 'undefined' ? SubjectService.getSubject(c.subjectId) : null;
            const prof = typeof ProfessorService !== 'undefined' ? ProfessorService.getProfessor(c.professorId) : null;
            const who = [c.classroom, prof ? `${prof.firstName || ''} ${prof.lastName || ''}`.trim() : ''].filter(Boolean).join(' · ');
            return { name: subject ? subject.name : (c.title || 'Clase'), who };
        };
        const todays = classes.filter(c => c.startDate && (c.startDate === today || (c.endDate && c.startDate <= today && c.endDate >= today)))
            .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
        if (todays.length) return todays.map(c => {
            const d = describe(c);
            return `<div class="spf-item"><span class="spf-time">${Utils.sanitizeHTML(`${c.startTime || ''}–${c.endTime || ''}`)}</span><span class="spf-item-main"><strong>${Utils.sanitizeHTML(d.name)}</strong>${d.who ? `<span>${Utils.sanitizeHTML(d.who)}</span>` : ''}</span></div>`;
        }).join('');
        const next = classes.filter(c => c.startDate && c.startDate > today)
            .sort((a, b) => a.startDate.localeCompare(b.startDate) || (a.startTime || '').localeCompare(b.startTime || ''))[0];
        if (!next) return '<p class="spf-note">Hoy no hay clase.</p>';
        const d = describe(next);
        return `<p class="spf-note">Hoy no hay clase. La próxima:</p>
            <div class="spf-item"><span class="spf-time">${Utils.sanitizeHTML(this.fmtDate(next.startDate, { weekday: 'short', day: 'numeric', month: 'short' }))} · ${Utils.sanitizeHTML(next.startTime || '')}</span><span class="spf-item-main"><strong>${Utils.sanitizeHTML(d.name)}</strong>${d.who ? `<span>${Utils.sanitizeHTML(d.who)}</span>` : ''}</span></div>`;
    },

    renderPendingTasks(tasks) {
        if (!tasks.pending.length) return `<p class="spf-note">${this.isOwn() ? 'Lo tienes todo entregado.' : 'No tiene tareas pendientes.'}</p>`;
        const own = this.isOwn();
        const now = new Date();
        return tasks.pending.slice(0, 4).map(({ task, deadline, late }) => {
            const subject = task.subjectId && typeof SubjectService !== 'undefined' ? SubjectService.getSubject(task.subjectId)?.name : '';
            const days = deadline ? Math.ceil((deadline - now) / 86400000) : null;
            const when = deadline ? (late ? `venció ${this.fmtDate(deadline, { day: 'numeric', month: 'short' })}` : `entrega ${this.fmtDate(deadline, { day: 'numeric', month: 'short' })}`) : 'sin fecha';
            const badge = late ? this.pill('Fuera de plazo', 'warn') : days !== null ? this.pill(days <= 0 ? 'Hoy' : days === 1 ? 'Mañana' : `${days} días`, 'soft') : '';
            return `<div class="spf-item${late ? ' is-late' : ''}">
                <span class="spf-item-main"><strong>${Utils.sanitizeHTML(task.title || 'Tarea')}</strong><span>${Utils.sanitizeHTML([subject, when].filter(Boolean).join(' · '))}</span></span>
                <span class="spf-item-end">${badge}${own ? `<button type="button" class="spf-btn is-primary is-small" ${UIEvents.attr("click", function(event,captured){App.navigate('tareas');}, [])}>Entregar</button>` : ''}</span>
            </div>`;
        }).join('') + (tasks.pending.length > 4 ? `<p class="spf-note">Y ${tasks.pending.length - 4} más.</p>` : '');
    },

    renderAsistencia() {
        const records = this._getAttendanceRecords();
        // Older records need the classes of the student's formations for those dates, loaded on demand.
        const dates = records.map(r => r.date).filter(d => /^\d{4}-\d{2}-\d{2}/.test(d || '')).sort();
        if (dates.length && typeof FileStorage !== 'undefined' && FileStorage.needClasses) {
            const student = this.student();
            const redraw = () => { if (this.tab === 'asistencia') this.switchTab('asistencia'); };
            (student?.formations || []).forEach(formationId => FileStorage.needClasses({ from: dates[0].slice(0, 10), to: dates[dates.length - 1].slice(0, 10), formationId }, redraw));
        }
        if (!records.length) return `<section class="spf-card"><p class="spf-note">No hay registros de asistencia todavía.</p></section>`;
        const allSessions = typeof ClassService !== 'undefined' ? ClassService.getClasses() : [];
        const label = { present: 'Asistió', absent: 'Faltó', justified: 'Justificada' };
        const tone = { present: 'ok', absent: 'bad', justified: 'warn' };
        const count = s => records.filter(r => r.status === s).length;
        const filters = [['all', 'Todas'], ['absent', `Faltó · ${count('absent')}`], ['justified', `Justificadas · ${count('justified')}`], ['present', `Asistió · ${count('present')}`]];
        const rows = records.filter(r => this.attFilter === 'all' || r.status === this.attFilter).sort((a, b) => String(b.date).localeCompare(String(a.date)));
        return `
            <section class="spf-card is-flush" aria-labelledby="spf-att-title">
                <div class="spf-card-head is-bar">
                    <h2 class="spf-h2" id="spf-att-title">${this.isOwn() ? 'Mi asistencia' : 'Asistencia'} · ${records.length} ${records.length === 1 ? 'sesión' : 'sesiones'}</h2>
                    <div class="spf-filter" role="group" aria-label="Filtrar sesiones">
                        ${filters.map(([id, text]) => `<button type="button" class="${this.attFilter === id ? 'is-on' : ''}" aria-pressed="${this.attFilter === id}" ${UIEvents.attr("click", function(event,captured){StudentProfileView.attFilter=String(captured[0]);StudentProfileView.switchTab('asistencia');}, [id])}>${text}</button>`).join('')}
                    </div>
                </div>
                <div class="spf-scroll">
                    <table class="spf-table">
                        <thead><tr><th scope="col">Fecha</th><th scope="col">Asignatura</th><th scope="col" class="spf-col-hide">Profesor/a</th><th scope="col">Estado</th></tr></thead>
                        <tbody>
                            ${rows.length ? rows.map(r => {
                                const session = allSessions.find(s => s.id === r.sessionId);
                                const subject = session?.subjectId && typeof DataManager !== 'undefined' ? DataManager.getById(DataManager.KEYS.SUBJECTS, session.subjectId) : null;
                                const prof = session && typeof ProfessorService !== 'undefined' ? ProfessorService.getProfessor(session.professorId) : null;
                                const name = subject ? Utils.sanitizeHTML(subject.name) : session ? Utils.sanitizeHTML(session.title || 'Sesión') : '<span class="spf-muted">Sesión eliminada</span>';
                                const st = r.status || 'present';
                                return `<tr><td class="spf-mono">${Utils.sanitizeHTML(this.fmtDate(r.date))}</td><td><strong>${name}</strong></td><td class="spf-col-hide spf-muted">${prof ? Utils.sanitizeHTML(`${prof.firstName || ''} ${prof.lastName || ''}`.trim()) : '—'}</td><td>${this.pill(label[st], tone[st])}</td></tr>`;
                            }).join('') : '<tr><td colspan="4" class="spf-muted">Ninguna sesión con este estado.</td></tr>'}
                        </tbody>
                    </table>
                </div>
            </section>`;
    },

    renderTrabajos() {
        const own = this.isOwn();
        const works = this._data?.works;
        const tasks = this._data?.tasks || [];
        const head = `
            <div class="spf-card-head is-bar">
                <h2 class="spf-h2" id="spf-works-title">${own ? 'Mis trabajos' : 'Trabajos'}${works ? ` · ${works.filter(w => !w.superseded).length} ${works.filter(w => !w.superseded).length === 1 ? 'entregado' : 'entregados'}` : ''}</h2>
                ${own ? `<button type="button" class="spf-btn is-primary is-small" ${UIEvents.attr("click", function(event,captured){App.navigate('tareas');}, [])}><i class="fas fa-upload" aria-hidden="true"></i>Entregar trabajo</button>` : '<span class="spf-muted">El alumno los sube desde su portal</span>'}
            </div>`;
        if (!this._data) return `<section class="spf-card is-flush" aria-labelledby="spf-works-title">${head}<p class="spf-note spf-pad">Cargando trabajos…</p></section>`;
        if (!works) return `<section class="spf-card is-flush" aria-labelledby="spf-works-title">${head}<p class="spf-note spf-pad">No se han podido cargar los trabajos.</p></section>`;
        if (!works.length) return `<section class="spf-card is-flush" aria-labelledby="spf-works-title">${head}<p class="spf-note spf-pad">${own ? 'Todavía no has entregado ningún trabajo.' : 'El alumno no ha subido ningún trabajo todavía.'}</p></section>`;
        const ext = w => (String(w.originalName || '').split('.').pop() || 'archivo').slice(0, 4).toUpperCase();
        const size = b => b < 1024 ? b + ' B' : b < 1048576 ? (b / 1024).toFixed(1) + ' KB' : (b / 1048576).toFixed(1) + ' MB';
        return `
            <section class="spf-card is-flush" aria-labelledby="spf-works-title">
                ${head}
                <ul class="spf-rows">
                    ${works.map(w => {
                        const task = w.taskId ? tasks.find(t => t.id === w.taskId) : null;
                        const late = task?.deadline && new Date(w.uploadedAt) > new Date(task.deadline);
                        const meta = [w.subject, task ? `Tarea: ${task.title}` : '', w.uploadedAt ? this.fmtDate(w.uploadedAt) : '', size(w.size || 0)].filter(Boolean).join(' · ');
                        const state = w.superseded ? this.pill('Histórico', 'soft') : task ? this.pill(late ? 'Fuera de plazo' : 'En plazo', late ? 'warn' : 'ok') : '';
                        return `<li class="spf-row${w.superseded ? ' is-old' : ''}">
                            <span class="spf-ext" aria-hidden="true">${Utils.sanitizeHTML(ext(w))}</span>
                            <span class="spf-item-main"><strong>${Utils.sanitizeHTML(w.title || w.originalName || 'Trabajo')}</strong><span>${Utils.sanitizeHTML(meta)}</span>${w.notes ? `<span class="spf-row-note">${Utils.sanitizeHTML(w.notes)}</span>` : ''}</span>
                            ${state}
                            <a class="spf-icon-btn" href="/api/student-works/${encodeURIComponent(this.studentId)}/download/${encodeURIComponent(w.id)}" target="_blank" rel="noopener" aria-label="Descargar ${Utils.sanitizeHTML(w.title || w.originalName || '')}" title="Descargar"><i class="fas fa-download" aria-hidden="true"></i></a>
                            ${own ? '' : `<button type="button" class="spf-icon-btn is-danger" aria-label="Borrar ${Utils.sanitizeHTML(w.title || w.originalName || '')}" title="Borrar" ${UIEvents.attr("click", function(event,captured){StudentProfileView.deleteServerWork(String(captured[0]),String(captured[1]));}, [this.studentId, w.id])}><i class="fas fa-trash" aria-hidden="true"></i></button>`}
                        </li>`;
                    }).join('')}
                </ul>
            </section>`;
    },

    async deleteServerWork(studentId, fileId) {
        const result = await SafeHTML.fire({
            title: '¿Eliminar trabajo?',
            text: 'Esta acción no se puede deshacer.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            reverseButtons: true,
            focusCancel: true
        });
        if (!result.isConfirmed) return;
        const token = typeof AuthManager !== 'undefined' ? AuthManager.getCsrfToken() : null;
        try {
            const res = await fetch('/api/student-works/' + studentId + '/' + fileId, {
                method: 'DELETE',
                headers: token ? { 'X-SICAF-CSRF': token } : {}
            });
            const data = await res.json();
            if (data.success) {
                Utils.showToast('Trabajo eliminado', 'info');
                this.loadData();
            } else {
                Utils.showToast('Error al eliminar: ' + (data.error || ''), 'error');
            }
        } catch(e) {
            Utils.showToast('Error de red', 'error');
        }
    },

    renderTutorias() {
        const own = this.isOwn();
        const list = this._data?.tutorias;
        const head = `
            <div class="spf-card-head is-bar">
                <h2 class="spf-h2" id="spf-tut-title">${own ? 'Mis tutorías' : 'Tutorías'}${list ? ` · ${list.length}` : ''}</h2>
                <button type="button" class="spf-link" ${UIEvents.attr("click", function(event,captured){App.navigate(AuthManager.isStudent()||AuthManager.isProfessor()?'tutorias':'tutorias-admin');}, [])}>${own ? 'Pedir tutoría' : 'Ir a Tutorías'} ›</button>
            </div>`;
        if (!list) return `<section class="spf-card is-flush" aria-labelledby="spf-tut-title">${head}<p class="spf-note spf-pad">${this._data ? 'No se han podido cargar las tutorías.' : 'Cargando tutorías…'}</p></section>`;
        if (!list.length) return `<section class="spf-card is-flush" aria-labelledby="spf-tut-title">${head}<p class="spf-note spf-pad">${own ? 'Todavía no has pedido ninguna tutoría.' : 'No hay tutorías registradas para este alumno.'}</p></section>`;
        const state = {
            pending: ['Pendiente', 'warn'], proposed_1: [own ? 'Te proponen fecha' : 'Fecha propuesta', 'soft'], proposed_3: [own ? 'Elige una fecha' : '3 opciones propuestas', 'soft'],
            confirmed: ['Confirmada', 'ok'], rejected_1: ['Rechazada', 'bad'], cancelled: ['Cancelada', 'soft']
        };
        const rows = [...list].sort((a, b) => String(b.confirmedDate || b.proposedDate || b.createdAt || '').localeCompare(String(a.confirmedDate || a.proposedDate || a.createdAt || '')));
        return `
            <section class="spf-card is-flush" aria-labelledby="spf-tut-title">
                ${head}
                <ul class="spf-rows">
                    ${rows.map(t => {
                        const date = t.status === 'confirmed' ? [t.confirmedDate && this.fmtDate(t.confirmedDate, { day: 'numeric', month: 'short' }), t.confirmedTime].filter(Boolean).join(' · ')
                            : t.status === 'proposed_1' ? [t.proposedDate && this.fmtDate(t.proposedDate, { day: 'numeric', month: 'short' }), t.proposedTime].filter(Boolean).join(' · ')
                            : (t.createdAt ? this.fmtDate(t.createdAt, { day: 'numeric', month: 'short' }) : '');
                        const [text, tone] = state[t.status] || [t.status || '—', 'soft'];
                        return `<li class="spf-row">
                            <span class="spf-time">${Utils.sanitizeHTML(date || '—')}</span>
                            <span class="spf-item-main"><strong>${Utils.sanitizeHTML(t.professorName || 'Profesor/a')}</strong><span>${Utils.sanitizeHTML(t.requestNotes || '')}</span>${t.rejectionReason ? `<span class="spf-row-note is-bad">Motivo del rechazo: ${Utils.sanitizeHTML(t.rejectionReason)}</span>` : ''}</span>
                            ${this.pill(text, tone)}
                        </li>`;
                    }).join('')}
                </ul>
            </section>`;
    },

    documents(student) {
        return [
            ['DNI / NIE · anverso', student.dniFile, 'De la solicitud'],
            ['DNI / NIE · reverso', student.dniBackFile, 'De la solicitud'],
            ['Foto de perfil', student.photo, 'De la solicitud'],
            ['Justificante de pago', student.justificantePagoFile, 'Pago'],
            ['DNI frontal (financiador)', student.dniFrontalFile, 'Pago'],
            ['DNI trasera (financiador)', student.dniTraseraFile, 'Pago'],
            ['Solicitud de pago fraccionado', student.solicitudFraccionadoFile, 'Pago'],
            ['Declaración de ingresos', student.declaracionIngresosFile, 'Pago']
        ].filter(d => d[1]).map(([label, file, sub]) => ({ label, file, sub, ext: (String(file).split('.').pop() || '').slice(0, 4).toUpperCase() }));
    },

    renderAside(student) {
        const own = this.isOwn();
        const docs = this.documents(student);
        const docsCard = `
            <section class="spf-card" aria-labelledby="spf-docs-title">
                <div class="spf-card-head"><h2 class="spf-h2" id="spf-docs-title">${own ? 'Mis documentos' : 'Documentos'}</h2>${docs.length ? '<span class="spf-muted">Se abren aquí</span>' : ''}</div>
                ${docs.length ? docs.map(d => `<button type="button" class="spf-doc" ${UIEvents.attr("click", function(event,captured){StudentProfileView.viewDocument(String(captured[0]),String(captured[1]));}, [d.file, d.label])}>
                    <span class="spf-ext is-small" aria-hidden="true">${Utils.sanitizeHTML(d.ext)}</span>
                    <span class="spf-item-main"><strong>${Utils.sanitizeHTML(d.label)}</strong><span>${Utils.sanitizeHTML(d.sub)}</span></span>
                    <i class="fas fa-chevron-right" aria-hidden="true"></i>
                </button>`).join('') : '<p class="spf-note">Todavía no hay documentos.</p>'}
            </section>`;
        const requests = typeof WarehouseService !== 'undefined' ? WarehouseService.getStudentRequests(student.id) : [];
        const whState = { pending: ['Pedido', 'soft'], approved: ['Aprobado', 'ok'], picked_up: ['Prestado', 'ok'], returned: ['Devuelto', 'soft'], rejected: ['Rechazado', 'bad'] };
        const active = [...requests].sort((a, b) => Number(a.status === 'returned') - Number(b.status === 'returned') || String(b.dates?.from || '').localeCompare(String(a.dates?.from || ''))).slice(0, 3);
        const material = active.length ? active.map(r => {
            const items = Array.isArray(r.items) ? r.items : [];
            const names = items.map(i => i.name).filter(Boolean);
            const what = names.length ? names.slice(0, 2).join(' + ') + (names.length > 2 ? ` y ${names.length - 2} más` : '') : `${items.length} ${items.length === 1 ? 'artículo' : 'artículos'}`;
            const when = r.status === 'picked_up' && r.dates?.to ? `Devolver el ${this.fmtDate(r.dates.to, { weekday: 'long', day: 'numeric', month: 'short' })}` : r.dates?.from ? `Para el ${this.fmtDate(r.dates.from, { day: 'numeric', month: 'short' })}` : '';
            const [text, tone] = whState[r.status] || [r.status || '—', 'soft'];
            return `<div class="spf-item"><span class="spf-item-main"><strong>${Utils.sanitizeHTML(what)}</strong>${when ? `<span>${Utils.sanitizeHTML(when)}</span>` : ''}</span>${this.pill(text, tone)}</div>`;
        }).join('') : `<p class="spf-note">${own ? 'No tienes material pedido.' : 'Sin peticiones al almacén.'}</p>`;
        if (own) return `
            <section class="spf-card is-alert" aria-labelledby="spf-todo-title">
                <h2 class="spf-h2" id="spf-todo-title">Tienes pendiente</h2>
                <div id="spf-todo"><p class="spf-note">Mirando…</p></div>
            </section>
            <section class="spf-card" aria-labelledby="spf-mat-title">
                <div class="spf-card-head"><h2 class="spf-h2" id="spf-mat-title">Material del almacén</h2><button type="button" class="spf-link" ${UIEvents.attr("click", function(event,captured){App.navigate('almacen');}, [])}>Pedir ›</button></div>
                ${material}
            </section>
            ${docsCard}`;
        const user = typeof AuthManager.getStudentUser === 'function' ? AuthManager.getStudentUser(student.id) : null;
        const admin = AuthManager.isAdmin();
        const access = user ? `<div class="spf-kv"><code class="spf-code">${Utils.sanitizeHTML(user.username || '')}</code>${admin && student.email ? `<button type="button" class="spf-btn is-small" ${UIEvents.attr("click", function(event,captured){StudentView.sendAccess([String(captured[0])]);}, [student.id])}><i class="fas fa-paper-plane" aria-hidden="true"></i>Reenviar acceso</button>` : ''}</div>`
            : student.email ? `<div class="spf-kv"><span class="spf-muted">Todavía sin cuenta</span>${admin ? `<button type="button" class="spf-btn is-small" ${UIEvents.attr("click", function(event,captured){StudentView.giveAccess([String(captured[0])]);}, [student.id])}><i class="fas fa-key" aria-hidden="true"></i>Dar acceso</button>` : ''}</div>`
            : `<p class="spf-note">${this.pill('Sin correo', 'warn')} Añade su correo en la ficha para poder darle acceso.</p>`;
        const payment = student.paymentType ? (student.paymentType === 'unico' ? 'Pago único' : `Fraccionado · ${student.paymentMonths || '?'} meses`) : 'Sin indicar';
        return `
            <section class="spf-card" aria-labelledby="spf-access-title">
                <h2 class="spf-h2" id="spf-access-title">Acceso a la app</h2>
                ${access}
            </section>
            ${docsCard}
            <section class="spf-card" aria-labelledby="spf-pay-title">
                <h2 class="spf-h2" id="spf-pay-title">Pago y almacén</h2>
                <div class="spf-kv"><span class="spf-muted">Pago</span><strong>${Utils.sanitizeHTML(payment)}</strong></div>
                ${material}
            </section>`;
    },

    renderTodo(tasks) {
        const absent = this._getAttendanceRecords().filter(r => r.status === 'absent');
        const proposals = (this._data?.tutorias || []).filter(t => ['proposed_1', 'proposed_3'].includes(t.status));
        const item = (count, tone, title, sub, view) => `<button type="button" class="spf-todo" ${UIEvents.attr("click", function(event,captured){App.navigate(String(captured[0]));}, [view])}>
            <span class="spf-dot is-${tone}" aria-hidden="true">${count}</span>
            <span class="spf-item-main"><strong>${title}</strong><span>${Utils.sanitizeHTML(sub)}</span></span>
            <i class="fas fa-chevron-right" aria-hidden="true"></i>
        </button>`;
        const rows = [
            tasks && tasks.pending.length ? item(tasks.pending.length, 'warn', tasks.pending.length === 1 ? 'Trabajo por entregar' : 'Trabajos por entregar', tasks.late.length ? `${tasks.late.length} ya fuera de plazo` : `El primero: ${tasks.pending[0].task.title || 'tarea'}`, 'tareas') : '',
            absent.length ? item(absent.length, 'bad', absent.length === 1 ? 'Falta sin justificar' : 'Faltas sin justificar', absent.length === 1 ? this.fmtDate(absent[0].date) : 'Sube el justificante', 'justificantes') : '',
            proposals.length ? item(proposals.length, 'teal', proposals.length === 1 ? 'Tutoría con fecha propuesta' : 'Tutorías con fecha propuesta', 'Contesta para confirmarla', 'tutorias') : ''
        ].filter(Boolean);
        return rows.length ? rows.join('') : '<p class="spf-note">Nada pendiente: lo tienes todo al día.</p>';
    },

    renderNotas() {
        return `<section class="spf-published-grades" aria-labelledby="student-grades-title">
            <header class="spf-grades-header">
                <div><h3 id="student-grades-title">Calificaciones</h3>
                    <p>Evaluaciones publicadas del alumno</p></div>
                <span class="spf-grades-readonly"><i class="fas fa-lock" aria-hidden="true"></i> Solo consulta</span>
            </header>
            <div id="student-published-grades" aria-live="polite" aria-busy="true">
                <p role="status">Cargando calificaciones del alumno…</p>
            </div>
        </section>`;
    },

    async loadNotas() {
        const container = document.getElementById('student-published-grades');
        if (!container) return;
        const studentId = this.studentId;
        const request = this._notesRequest = (this._notesRequest || 0) + 1;
        const current = () => request === this._notesRequest && studentId === this.studentId &&
            document.getElementById('student-published-grades') === container;
        container.setAttribute('aria-busy', 'true');
        SafeHTML.set(container, '<p role="status">Cargando calificaciones del alumno…</p>');
        try {
            const response = await fetch('/api/calificaciones?studentId=' + encodeURIComponent(studentId), {cache:'no-store'});
            if (!response.ok) throw Object.assign(new Error('Calificaciones no disponibles'), {status:response.status});
            const data = await response.json();
            if (!data.success || !Array.isArray(data.calificaciones) || data.calificaciones.some(c => !c || c.nota?.studentId !== studentId)) {
                throw new Error('Respuesta de calificaciones no válida');
            }
            if (!current()) return;
            SafeHTML.set(container, this.renderPublishedGrades(data.calificaciones));
        } catch (error) {
            if (!current()) return;
            const message = error.status === 403 ? 'No tienes permiso para consultar estas calificaciones.' :
                error.status === 401 ? 'Tu sesión ha caducado. Vuelve a iniciar sesión.' :
                'No se han podido cargar las calificaciones. Comprueba la conexión y vuelve a intentarlo.';
            SafeHTML.set(container, `<div role="alert"><p>${message}</p>` +
                (![401,403].includes(error.status) ? `<button type="button" class="btn btn-secondary btn-sm" ${UIEvents.attr("click", function(event,captured){StudentProfileView.loadNotas();}, [])}>Reintentar</button>` : '') + '</div>');
        } finally {
            if (current()) container.setAttribute('aria-busy', 'false');
        }
    },

    renderPublishedGrades(grades) {
        if (!grades.length) return '<div class="spf-grades-empty"><p>Este alumno todavía no tiene calificaciones publicadas.</p></div>';
        const escape = value => Utils.sanitizeHTML(String(value ?? ''));
        const periods = {'1Trimestre':'Primer trimestre','2Trimestre':'Segundo trimestre','3Trimestre':'Tercer trimestre','Final':'Evaluación final'};
        const order = {'1Trimestre':1,'2Trimestre':2,'3Trimestre':3,'Final':4};
        const fields = [['Técnica','tecnica'],['Lenguaje y estética','lenguaje'],['Compromiso','compromiso'],['Colectivo','colectivo'],['Adaptabilidad','adaptabilidad']];
        const score = value => {
            const state = {'APTO':'is-pass','AVISO':'is-warning','NO APTO':'is-fail'}[value] || 'is-empty';
            return `<span class="spf-grade-mark ${state}">${escape(value || 'Sin evaluar')}</span>`;
        };
        const groups = new Map();
        [...grades].sort((a,b) => String(b.year || '').localeCompare(String(a.year || '')) ||
            String(a.formationName || '').localeCompare(String(b.formationName || ''), 'es') || (order[a.period] || 0) - (order[b.period] || 0))
            .forEach(acta => {
                const key = JSON.stringify([acta.year, acta.formationId || acta.formationName]);
                if (!groups.has(key)) groups.set(key, []);
                groups.get(key).push(acta);
            });
        return [...groups.values()].map((actas, index) => {
            const first = actas[0];
            const name = escape(first.formationName || 'Formación');
            const year = escape(first.year || 'sin especificar');
            return `<article class="spf-gradebook" aria-labelledby="spf-gradebook-${index}">
                <header class="spf-gradebook-heading">
                    <div><h4 id="spf-gradebook-${index}">${Utils.sanitizeHTML(name)}</h4><p>Curso ${year}</p></div>
                    <span class="spf-gradebook-count">${actas.length} ${actas.length === 1 ? 'acta publicada' : 'actas publicadas'}</span>
                </header>
                <div class="spf-gradebook-scroll" role="region" aria-label="Calificaciones de ${Utils.sanitizeHTML(name)}, curso ${year}" tabindex="0">
                    <table class="spf-gradebook-table" style="--spf-period-count: ${actas.length}">
                        <caption class="spf-visually-hidden">Calificaciones publicadas de ${Utils.sanitizeHTML(name)}, curso ${year}, por periodo y criterio</caption>
                        <thead><tr><th scope="col">Evaluación</th>${actas.map(c => `<th scope="col"${c.period === 'Final' ? ' class="spf-final-period"' : ''}>${escape(periods[c.period] || c.period)}</th>`).join('')}</tr></thead>
                        <tbody>${fields.map(([label,key]) => `<tr><th scope="row">${label}</th>${actas.map(c => `<td>${score(c.nota[key])}</td>`).join('')}</tr>`).join('')}
                            <tr class="spf-global-grade"><th scope="row">Calificación global</th>${actas.map(c => `<td>${score(c.nota.calificacion)}</td>`).join('')}</tr>
                            <tr class="spf-gradebook-notes"><th scope="row">Observaciones<span>y tutorías</span></th>${actas.map(c => `<td>${c.nota.tutorias ? escape(c.nota.tutorias) : '<span class="spf-no-observations">Sin observaciones</span>'}</td>`).join('')}</tr>
                        </tbody>
                    </table>
                </div>
            </article>`;
        }).join('');
    },

    // Popup de vista previa para un documento de la solicitud (DNI escaneado,
    // foto de perfil...) — antes abría el archivo en una pestaña nueva; ahora
    // se ve dentro de la propia app, con un botón de descarga en el propio
    // popup. Reutiliza el ModalManager genérico ya usado en el resto de la app.
    async viewDocument(fileRelativePath, label) {
        const studentId=this.studentId, session=AuthManager.getCsrfToken();
        const request=this._documentRequest=(this._documentRequest||0)+1;
        let blobUrl;
        try {
            const {blob}=await PrivateDownloads.fetchFile(fileRelativePath,{profile:true});
            const pdf=blob.type==='application/pdf';
            if(!pdf&&!/^image\/(png|jpeg|gif|webp)$/.test(blob.type))throw new Error('Formato de vista previa no permitido');
            if(pdf&&await blob.slice(0,5).text()!=='%PDF-')throw new Error('PDF no válido');
            if(this.studentId!==studentId||request!==this._documentRequest||AuthManager.getCsrfToken()!==session)return;
            blobUrl=URL.createObjectURL(blob);
            const node=document.createElement(pdf?'iframe':'img');
            node.src=blobUrl;node.title=label;node.style.cssText=pdf?'width:100%;height:65vh;border:none;':'max-width:100%;max-height:65vh;object-fit:contain;';
            if(!pdf)node.alt=label;
            ModalManager.show(label,'<div id="student-document-preview" style="text-align:center;"></div>',[
                {text:'Descargar',class:'btn-primary',action:()=>this.downloadDocument(fileRelativePath)},
                {text:'Cerrar',class:'btn-secondary',action:()=>ModalManager.close()}
            ],()=>URL.revokeObjectURL(blobUrl));
            document.getElementById('student-document-preview').replaceChildren(node);
        }catch(error){if(blobUrl)URL.revokeObjectURL(blobUrl);Utils.showToast(error.message,'error');}
    },

    downloadDocument(fileRelativePath) {
        return PrivateDownloads.download(fileRelativePath,{profile:true});
    },

    renderDatos() {
        const student = this.student();
        if (!student) return '';
        const own = this.isOwn();
        const birth = student.fechaNacimiento ? (() => {
            const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(student.fechaNacimiento);
            if (!m) return student.fechaNacimiento;
            const now = new Date(), age = now.getFullYear() - +m[1] - ((now.getMonth() + 1 < +m[2] || (now.getMonth() + 1 === +m[2] && now.getDate() < +m[3])) ? 1 : 0);
            return `${this.fmtDate(student.fechaNacimiento)} · ${age} años`;
        })() : '';
        const fields = [
            ['Nombre completo', `${student.firstName || ''} ${student.lastName || ''}`.trim()],
            ['DNI / NIE', student.dni],
            ['Fecha de nacimiento', birth],
            ['Correo', student.email],
            ['Teléfono', student.phone],
            ['Sexo', student.sexo],
            ['Dirección', student.address],
            ['Localidad', [student.localidad, student.provincia].filter(Boolean).join(' · ')],
            ['Código postal', [student.codigoPostal, student.pais].filter(Boolean).join(' · ')],
            ['Matrícula', student.enrollmentDate ? this.fmtDate(student.enrollmentDate) : ''],
            ['Cómo nos conoció', student.comoNosConociste],
            // «EDICIÓN / FOTO / DIRECCIÓN» → «1. Edición · 2. Foto · 3. Dirección».
            ['Preferencias de curso', student.coursePreferences && student.coursePreferences.split('/').map((p, i) => `${i + 1}. ${p.trim()}`).join(' · ')]
        ].filter(f => f[1]);
        const dl = list => `<dl class="spf-dl">${list.map(([label, value]) => `<div><dt>${Utils.sanitizeHTML(label)}</dt><dd>${Utils.sanitizeHTML(String(value))}</dd></div>`).join('')}</dl>`;

        // Carta de presentación y estudios: texto libre del formulario o, si subió un archivo, el archivo.
        const text = (label, body, file) => `
            <section class="spf-card">
                <h2 class="spf-h2 is-small">${label}</h2>
                ${body ? `<p class="spf-text">${Utils.sanitizeHTML(body)}</p>`
                    : `<button type="button" class="spf-link" ${UIEvents.attr("click", function(event,captured){StudentProfileView.viewDocument(String(captured[0]),String(captured[1]));}, [file, label])}>Ver el archivo adjunto ›</button>`}
            </section>`;
        const texts = [
            (student.presentacionText || student.presentacionFile) ? text('Carta de presentación', student.presentacionText, student.presentacionFile) : '',
            (student.estudiosText || student.estudiosFile) ? text('Estudios', student.estudiosText, student.estudiosFile) : ''
        ].filter(Boolean);

        // Datos del tutor legal: solo existen si el alumno era menor de edad al matricularse.
        const tutor = [
            ['Nombre', student.tutorNombre], ['Teléfono', student.tutorTelefono], ['DNI', student.tutorDni], ['Correo', student.tutorEmail],
            ['Dirección', student.tutorDireccion], ['Municipio', [student.tutorMunicipio, student.tutorProvincia].filter(Boolean).join(' · ')],
            ['Código postal', [student.tutorCodigoPostal, student.tutorPais].filter(Boolean).join(' · ')]
        ].filter(f => f[1]);

        return `
            <section class="spf-card" aria-labelledby="spf-data-title">
                <div class="spf-card-head">
                    <h2 class="spf-h2" id="spf-data-title">${own ? 'Mis datos' : 'Datos personales'}</h2>
                    ${own ? '<span class="spf-muted">¿Algo no está bien? Escribe a secretaría</span>'
                        : this.canManage() ? `<button type="button" class="spf-link" ${UIEvents.attr("click", function(event,captured){StudentView.openEditModal(String(captured[0]));}, [student.id])}>Editar ›</button>` : ''}
                </div>
                ${fields.length ? dl(fields) : '<p class="spf-note">Sin datos en la ficha.</p>'}
            </section>
            ${texts.length ? `<div class="spf-two">${texts.join('')}</div>` : ''}
            ${tutor.length ? `<section class="spf-card" aria-labelledby="spf-tutor-title"><h2 class="spf-h2" id="spf-tutor-title">Tutor legal</h2>${dl(tutor)}</section>` : ''}`;
    },

    _getAttendanceRecords() {
        const results = [];
        const sid = this.studentId;

        // Primary source: ecpv_attendance array saved by AttendanceService
        try {
            const raw = BrowserState.getItem('ecpv_attendance');
            if (raw) {
                const allRecords = JSON.parse(raw);
                if (Array.isArray(allRecords)) {
                    allRecords.forEach(r => {
                        let status = null;
                        if (Array.isArray(r.present) && r.present.includes(sid)) status = 'present';
                        else if (Array.isArray(r.absent) && r.absent.includes(sid)) status = 'absent';
                        else if (Array.isArray(r.justified) && r.justified.includes(sid)) status = 'justified';
                        if (status) {
                            results.push({
                                sessionId: r.classSessionId,
                                date: r.date,
                                status
                            });
                        }
                    });
                }
            }
        } catch(e) {
            console.error('Error reading attendance records:', e);
        }

        return results;
    },

    openAddWorkModal() { this.saveWork(); },
    saveWork() {
        Utils.showToast("Este formulario antiguo no guarda en el servidor. Usa el módulo de Tareas y entregas.", 'warning');
    },
    async deleteWork(i) {
        Utils.showToast("Este formulario antiguo no guarda en el servidor. Usa el módulo de Tareas y entregas.", 'warning');
    }
};
